import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { StorefrontHunterIcon } from "./icons";
import { RatingStar } from "@/components/common/primitives";
import "./sellerWatch.css";
import {
  addWatchSeller,
  fetchSellerWatch,
  markWatchSellerRead,
  removeWatchSeller,
  resolveWatchSeller,
  updateWatchSeller,
} from "@/lib/sellerWatch/api";
import {
  FILTER_OPTIONS,
  SORT_OPTIONS,
  addTagUnique,
  allKnownTags,
  buildDailyActivity,
  formatBadgeCount,
  productsPostedLabel,
  relativeTime,
  unreadTotal,
  visibleSellers,
} from "@/lib/sellerWatch/model";

const POPOVER_WIDTH = 150;

function BookmarkIcon({ filled }) {
  return filled ? (
    <svg className="sw-bookmark-icon" viewBox="0 0 24 24" aria-hidden="true" data-testid="sw-bookmark-filled">
      <path d="M6 3.75A1.75 1.75 0 0 1 7.75 2h8.5A1.75 1.75 0 0 1 18 3.75V22l-6-3.9L6 22V3.75Z" fill="currentColor" />
    </svg>
  ) : (
    <svg className="sw-bookmark-icon" viewBox="0 0 24 24" aria-hidden="true" data-testid="sw-bookmark-outline">
      <path
        d="M6.75 2.75h10.5v16.87L12 16.2l-5.25 3.42V2.75Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function errorCode(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object") return detail.code || null;
  return typeof detail === "string" ? detail : null;
}

/* ── Activity graph ─────────────────────────────────────────────────────── */

function ActivityGraph({ seller, days, activeKey, pinnedKey, onHover, onPin }) {
  const wrapRef = useRef(null);
  const barRefs = useRef({});
  const [left, setLeft] = useState(0);
  const sid = seller.seller_id;
  const max = Math.max(1, ...days.map((d) => d.count));
  const active = days.find((d) => `${sid}:${d.key}` === activeKey) || null;

  useLayoutEffect(() => {
    if (!active) return;
    const bar = barRefs.current[active.key];
    const wrap = wrapRef.current;
    if (!bar || !wrap) return;
    const b = bar.getBoundingClientRect();
    const w = wrap.getBoundingClientRect();
    const desired = b.left - w.left + b.width / 2 - POPOVER_WIDTH / 2;
    const maxLeft = Math.max(0, w.width - POPOVER_WIDTH);
    setLeft(Math.max(0, Math.min(maxLeft, desired)));
  }, [active]);

  return (
    <div className="sw-graph" ref={wrapRef}>
      <div className="sw-spark" role="group" aria-label={`New products, last ${days.length} days`}>
        {days.map((d) => {
          const key = `${sid}:${d.key}`;
          const pct = d.count > 0 ? Math.max(12, Math.round((d.count / max) * 100)) : 0;
          const desc = d.monitored ? productsPostedLabel(d.count) : "Not monitored yet";
          return (
            <button
              key={d.key}
              ref={(el) => {
                barRefs.current[d.key] = el;
              }}
              type="button"
              className={`sw-spark-bar${d.count > 0 ? " has" : ""}${d.monitored ? "" : " unmonitored"}${
                key === activeKey ? " active" : ""
              }${key === pinnedKey ? " pinned" : ""}`}
              style={pct ? { "--bar-h": `${pct}%` } : undefined}
              aria-label={`${d.label}: ${desc}`}
              aria-pressed={key === pinnedKey}
              data-testid={`sw-day-${sid}-${d.key}`}
              onPointerEnter={(e) => e.pointerType !== "touch" && onHover(key)}
              onPointerLeave={(e) => e.pointerType !== "touch" && onHover(null)}
              onFocus={() => onHover(key)}
              onBlur={() => onHover(null)}
              onClick={(e) => {
                e.stopPropagation();
                onPin(key);
              }}
            />
          );
        })}
      </div>
      {active ? (
        <div
          className="sw-day-popover"
          style={{ left, width: POPOVER_WIDTH }}
          role="status"
          data-testid="sw-day-popover"
        >
          <div className="sw-day-date">{active.label}</div>
          <div className="sw-day-count">
            {active.monitored ? productsPostedLabel(active.count) : "Not monitored yet"}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ── Tag menu ───────────────────────────────────────────────────────────── */

function TagMenu({ seller, knownTags, onChange, onClose }) {
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  useEffect(() => inputRef.current?.focus(), []);
  const current = seller.tags || [];
  const lower = current.map((t) => t.toLowerCase());
  const query = q.replace(/\s+/g, " ").trim();
  const options = knownTags.filter(
    (t) => !lower.includes(t.toLowerCase()) && t.toLowerCase().includes(query.toLowerCase())
  );
  const exact = [...knownTags, ...current].some((t) => t.toLowerCase() === query.toLowerCase());
  const add = (tag) => {
    const next = addTagUnique(current, tag);
    // Case-insensitive duplicates (and blank input) return the same array: no request.
    if (next === current) {
      onClose();
      return;
    }
    onChange(next);
    onClose();
  };
  return (
    <div className="sw-popover sw-tag-menu" role="dialog" aria-label="Edit tags" onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        className="sw-tag-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && query) add(query);
        }}
        placeholder="Search or create tag"
        aria-label="Search or create tag"
        maxLength={32}
      />
      {current.length ? (
        <div className="sw-tag-current">
          {current.map((t) => (
            <span key={t} className="sw-tag">
              {t}
              <button
                type="button"
                aria-label={`Remove tag ${t}`}
                onClick={() => onChange(current.filter((x) => x !== t))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
      {options.slice(0, 8).map((t) => (
        <button key={t} type="button" className="sw-menu-item" onClick={() => add(t)}>
          {t}
        </button>
      ))}
      {query && !exact ? (
        <button type="button" className="sw-menu-item sw-create-tag" onClick={() => add(query)}>
          + Create “{query}”
        </button>
      ) : null}
    </div>
  );
}

/* ── Seller card ────────────────────────────────────────────────────────── */

function SellerCard({
  seller,
  selected,
  days,
  activeDayKey,
  pinnedDayKey,
  onHoverDay,
  onPinDay,
  openMenu,
  setOpenMenu,
  knownTags,
  onSelect,
  onSave,
  onTags,
  onToggleStatus,
  onMarkRead,
  onRemove,
}) {
  const sid = seller.seller_id;
  const tags = seller.tags || [];
  const paused = seller.status === "paused";
  const newCount = Number(seller.new_count) || 0;
  const [confirmRemove, setConfirmRemove] = useState(false);
  const menuOpen = openMenu === `card:${sid}`;
  const tagOpen = openMenu === `tag:${sid}`;
  useEffect(() => {
    if (!menuOpen) setConfirmRemove(false);
  }, [menuOpen]);
  const stop = (fn) => (e) => {
    e.stopPropagation();
    fn(e);
  };
  const lastChecked = relativeTime(seller.last_checked_at);
  const since = seller.monitoring_since ? new Date(seller.monitoring_since) : null;
  const recentlyStarted = since && Date.now() - since.getTime() < 14 * 24 * 60 * 60 * 1000;

  return (
    <article
      className={`sw-card${selected ? " selected" : ""}`}
      aria-current={selected ? "true" : undefined}
      data-testid={`sw-card-${sid}`}
      onClick={() => onSelect(seller)}
    >
      <div className="sw-card-top">
        <div className="min-w-0">
          <button
            type="button"
            className="sw-seller-name"
            onClick={stop(() => onSelect(seller))}
            title="View seller in Storefront Hunter"
          >
            {seller.seller_name || `Seller ${sid}`}
          </button>
          <div className="sw-seller-id">{sid}</div>
        </div>
        <div className="sw-rel">
          <button
            type="button"
            className="sw-kebab"
            aria-label="Seller actions"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            data-testid={`sw-kebab-${sid}`}
            onClick={stop(() => setOpenMenu(menuOpen ? null : `card:${sid}`))}
          >
            •••
          </button>
          {menuOpen ? (
            <div className="sw-popover sw-card-menu" role="menu" onClick={(e) => e.stopPropagation()}>
              {confirmRemove ? (
                <div className="sw-confirm">
                  <div>Remove {seller.seller_name || sid} from Seller Watch?</div>
                  <div className="sw-confirm-actions">
                    <button type="button" className="sw-btn" onClick={() => setConfirmRemove(false)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="sw-btn danger"
                      onClick={() => {
                        setOpenMenu(null);
                        onRemove(seller);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button type="button" role="menuitem" className="sw-menu-item" onClick={() => { setOpenMenu(null); onSelect(seller); }}>
                    View Seller
                  </button>
                  <button type="button" role="menuitem" className="sw-menu-item" onClick={() => setOpenMenu(`tag:${sid}`)}>
                    Edit Tags
                  </button>
                  <button type="button" role="menuitem" className="sw-menu-item" onClick={() => { setOpenMenu(null); onToggleStatus(seller); }}>
                    {paused ? "Resume Seller" : "Pause Seller"}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="sw-menu-item"
                    disabled={newCount <= 0}
                    onClick={() => { setOpenMenu(null); onMarkRead(seller); }}
                  >
                    Mark Activity Read
                  </button>
                  <div className="sw-divider" role="separator" />
                  <button type="button" role="menuitem" className="sw-menu-item danger" onClick={() => setConfirmRemove(true)}>
                    Remove Seller
                  </button>
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div className="sw-rating-row">
        <span>
          <RatingStar className="mr-0.5" />
          {seller.rating != null ? Number(seller.rating).toFixed(1) : "—"}
          {seller.review_count != null ? ` (${Number(seller.review_count).toLocaleString()})` : ""}
        </span>
        <span>
          {seller.product_count != null ? `${Number(seller.product_count).toLocaleString()} products` : "— products"}
        </span>
      </div>

      <div className="sw-tags-row">
        <button
          type="button"
          className={`sw-save${seller.saved ? " saved" : ""}`}
          aria-label={seller.saved ? "Unsave seller" : "Save seller"}
          aria-pressed={!!seller.saved}
          title={seller.saved ? "Unsave seller" : "Save seller"}
          data-testid={`sw-save-${sid}`}
          onClick={stop(() => onSave(seller, !seller.saved))}
        >
          <BookmarkIcon filled={!!seller.saved} />
        </button>
        {tags.slice(0, 2).map((t) => (
          <span key={t} className="sw-tag">
            {t}
          </span>
        ))}
        {tags.length > 2 ? <span className="sw-tag sw-tag-more">+{tags.length - 2}</span> : null}
        <span className="sw-rel">
          <button
            type="button"
            className="sw-add-tag"
            aria-haspopup="dialog"
            aria-expanded={tagOpen}
            data-testid={`sw-add-tag-${sid}`}
            onClick={stop(() => setOpenMenu(tagOpen ? null : `tag:${sid}`))}
          >
            + Tag
          </button>
          {tagOpen ? (
            <TagMenu
              seller={seller}
              knownTags={knownTags}
              onChange={(next) => onTags(seller, next)}
              onClose={() => setOpenMenu(null)}
            />
          ) : null}
        </span>
      </div>

      <div className="sw-state-row">
        <span className={`sw-status${paused ? " paused" : ""}`}>{paused ? "Paused" : "Active"}</span>
        {newCount > 0 ? <span className="sw-new-pill">+{formatBadgeCount(newCount)} New</span> : null}
      </div>

      {paused ? (
        <div className="sw-last-check">Monitoring paused</div>
      ) : (
        <>
          <div className="sw-activity-head">
            <span>New products · Last {days.length} days</span>
          </div>
          <ActivityGraph
            seller={seller}
            days={days}
            activeKey={activeDayKey}
            pinnedKey={pinnedDayKey}
            onHover={onHoverDay}
            onPin={onPinDay}
          />
          <div className="sw-last-check">
            {lastChecked ? `Last checked ${lastChecked}` : "Not checked yet — open the seller to start monitoring"}
            {recentlyStarted ? ` · Monitoring started ${since.toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
          </div>
        </>
      )}
    </article>
  );
}

/* ── Add seller dialog ──────────────────────────────────────────────────── */

function AddSellerDialog({ usage, limit, onClose, onAdded, onView }) {
  const [value, setValue] = useState("");
  const [resolved, setResolved] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [phase, setPhase] = useState("idle");
  const inputRef = useRef(null);
  const atLimit = usage >= limit;

  useEffect(() => inputRef.current?.focus(), []);
  useEffect(() => {
    const raw = value.trim();
    setResolved(null);
    setInvalid(false);
    if (!raw) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await resolveWatchSeller(raw);
        if (!cancelled) setResolved(res);
      } catch {
        if (!cancelled) setInvalid(true);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [value]);

  const already = !!resolved?.already_saved;
  const blockedByLimit = atLimit && !resolved?.in_history;
  const canAdd = !!resolved && !already && !blockedByLimit && phase === "idle";

  const submit = async () => {
    if (!canAdd) return;
    setPhase("adding");
    try {
      const res = await addWatchSeller(value.trim());
      setPhase("saved");
      onAdded(res);
    } catch (err) {
      const code = errorCode(err);
      setPhase("idle");
      if (code === "already_saved") setResolved((r) => ({ ...(r || {}), already_saved: true }));
      else if (code === "seller_watch_limit_reached") toast.error("Seller Watch limit reached");
      else toast.error("Couldn't add seller. Try again.");
    }
  };

  const profile = resolved?.profile || null;
  const label = phase === "adding" ? "Adding..." : phase === "saved" || already ? "Saved" : "Add Seller";

  return (
    <div className="sw-modal-backdrop" onMouseDown={onClose}>
      <div
        className="sw-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sw-add-title"
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="sw-add-dialog"
      >
        <h2 id="sw-add-title">Add Seller</h2>
        <div className="sw-sub">Save a Walmart storefront to Seller Watch.</div>
        <label className="sw-form-label" htmlFor="sw-add-input">
          Seller URL or ID
        </label>
        <input
          id="sw-add-input"
          ref={inputRef}
          className="sw-field"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setPhase("idle");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          placeholder="1816 or walmart.com/seller/1816"
          maxLength={300}
        />
        <div className={`sw-helper${invalid ? " warning" : ""}`}>
          {invalid ? "Enter a Walmart seller ID or walmart.com/seller/… URL." : "Accepts short IDs and Walmart seller URLs."}
        </div>
        {resolved ? (
          <div className="sw-detected" data-testid="sw-add-preview">
            <b>{profile?.seller_name || `Seller ${resolved.seller_id}`}</b>
            <div className="sw-helper">
              Seller ID {resolved.seller_id}
              {profile?.rating != null ? (
                <>
                  {" "}
                  · <RatingStar className="mr-0.5" />
                  {Number(profile.rating).toFixed(1)}
                </>
              ) : (
                ""
              )}
              {profile?.product_count != null ? ` · ${Number(profile.product_count).toLocaleString()} products` : ""}
            </div>
            {!profile?.seller_name ? (
              <div className="sw-helper">Seller details load from Walmart when you add it.</div>
            ) : null}
            {already ? <div className="sw-helper warning">Already saved in Seller Watch.</div> : null}
            {blockedByLimit ? (
              <div className="sw-helper warning">Seller Watch limit reached ({limit} sellers).</div>
            ) : null}
          </div>
        ) : null}
        <div className="sw-usage sw-usage-modal">
          <span>Seller Watch usage</span>
          <strong>
            {usage} / {limit} sellers
          </strong>
        </div>
        <div className="sw-modal-actions">
          <button type="button" className="sw-btn" onClick={onClose}>
            Cancel
          </button>
          {already ? (
            <button type="button" className="sw-btn" onClick={() => onView(resolved.seller_id)}>
              View Seller
            </button>
          ) : null}
          <button type="button" className="sw-btn primary" disabled={!canAdd} onClick={submit} data-testid="sw-add-confirm">
            {label}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Layout ─────────────────────────────────────────────────────────────── */

const DOCK_GAP = 12;

/**
 * Pins the dock to the global sidebar's right edge and insets the page root by
 * exactly the space the dock covers, so the existing content reflows instead of
 * being overlapped. Sidebar collapse, resize and centering are all measured.
 */
export function useSellerWatchDock(open, rootRef, dockRef) {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const measure = () => {
      const sidebar = document.querySelector('[data-testid="sidebar"]');
      const sRect = sidebar?.getBoundingClientRect();
      const sidebarRight = sRect && sRect.width > 0 ? Math.round(sRect.right) : 0;
      const header = document.querySelector('[data-testid="command-bar"]');
      const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().height)) : 0;
      root.style.setProperty("--sw-sidebar-right", `${sidebarRight}px`);
      root.style.setProperty("--sw-top", `${top}px`);
      const dock = dockRef.current;
      if (open && dock) {
        const dockRight = sidebarRight + dock.getBoundingClientRect().width;
        const rootLeft = root.getBoundingClientRect().left;
        root.style.setProperty("--sw-inset", `${Math.max(0, Math.round(dockRight + DOCK_GAP - rootLeft))}px`);
      } else {
        root.style.removeProperty("--sw-inset");
      }
    };
    measure();
    window.addEventListener("resize", measure);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      const sidebar = document.querySelector('[data-testid="sidebar"]');
      if (sidebar) ro.observe(sidebar);
      if (dockRef.current) ro.observe(dockRef.current);
    }
    return () => {
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [open, rootRef, dockRef]);
}

/* ── Seller Watch ───────────────────────────────────────────────────────── */

export function SellerWatchLauncher({ count, onClick }) {
  const badge = formatBadgeCount(count);
  return (
    <button
      type="button"
      className="sw-launcher"
      onClick={onClick}
      aria-label={badge ? `Open Seller Watch, ${badge} new products` : "Open Seller Watch"}
      title="Seller Watch"
      data-testid="sw-launcher"
    >
      <StorefrontHunterIcon className="sw-launcher-icon" />
      {badge ? (
        <span className="sw-launcher-badge" data-testid="sw-launcher-badge">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

/**
 * Launcher (closed) or docked column (open). Opening, closing, search, tabs,
 * filter, sort and menus never touch the network; only explicit actions do.
 */
export function SellerWatch({ open, onOpen, onClose, selectedSellerIds = [], onSelectSeller, refreshKey = 0, dockRef }) {
  const [state, setState] = useState({ status: "loading", sellers: [], limit: 50, usage: 0 });
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("all");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("recent");
  const [openMenu, setOpenMenu] = useState(null);
  const [hoverDay, setHoverDay] = useState(null);
  const [pinnedDay, setPinnedDay] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchSellerWatch();
      setState({
        status: "ready",
        sellers: data?.sellers || [],
        limit: Number(data?.limit) || 50,
        usage: Number(data?.usage) || (data?.sellers || []).length,
      });
    } catch {
      setState((s) => ({ ...s, status: s.sellers.length ? "ready" : "error" }));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  // Storage-only refresh when the dock opens; never a provider call.
  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const t = e.target;
      if (!t?.closest?.(".sw-spark-bar") && !t?.closest?.(".sw-day-popover")) setPinnedDay(null);
      if (!t?.closest?.(".sw-popover") && !t?.closest?.(".sw-menu-trigger") && !t?.closest?.(".sw-kebab") && !t?.closest?.(".sw-add-tag")) {
        setOpenMenu(null);
      }
    };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (adding) setAdding(false);
      else {
        setOpenMenu(null);
        setPinnedDay(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, adding]);

  const sellers = state.sellers;
  const now = useMemo(() => new Date(), [sellers]); // eslint-disable-line react-hooks/exhaustive-deps
  const visible = useMemo(
    () => visibleSellers(sellers, { query, tab, filter, sort }, now),
    [sellers, query, tab, filter, sort, now]
  );
  const daysBySeller = useMemo(() => {
    const out = {};
    for (const s of sellers) out[s.seller_id] = buildDailyActivity(s, now);
    return out;
  }, [sellers, now]);
  const knownTags = useMemo(() => allKnownTags(sellers), [sellers]);
  const unread = unreadTotal(sellers);
  const activityCount = sellers.filter((s) => (Number(s.new_count) || 0) > 0).length;

  const patchLocal = (id, fn) =>
    setState((s) => ({ ...s, sellers: s.sellers.map((x) => (x.seller_id === id ? fn(x) : x)) }));

  const optimistic = async (seller, next, call, failMsg) => {
    const prev = seller;
    patchLocal(seller.seller_id, () => next);
    try {
      await call();
      return true;
    } catch {
      patchLocal(seller.seller_id, () => prev);
      toast.error(failMsg);
      return false;
    }
  };

  /* Bookmark is a true toggle. Unsaving clears the flag but keeps the seller in
     Seller Watch (Remove Seller is the separate destructive action). */
  const onSave = (s, nextSaved) => {
    const target = nextSaved === undefined ? !s.saved : !!nextSaved;
    if (target === !!s.saved) return Promise.resolve(true);
    return optimistic(
      s,
      { ...s, saved: target },
      () => updateWatchSeller(s.seller_id, { saved: target }),
      target ? "Couldn't save seller" : "Couldn't unsave seller"
    ).then((ok) => ok && toast.success(target ? `${s.seller_name || s.seller_id} saved` : `${s.seller_name || s.seller_id} unsaved`));
  };
  const onTags = (s, tags) =>
    optimistic(s, { ...s, tags }, () => updateWatchSeller(s.seller_id, { tags }), "Couldn't update tags");
  const onToggleStatus = (s) => {
    const status = s.status === "paused" ? "active" : "paused";
    return optimistic(s, { ...s, status }, () => updateWatchSeller(s.seller_id, { status }), "Couldn't update seller").then(
      (ok) => ok && toast.success(`${s.seller_name || s.seller_id} ${status === "paused" ? "paused" : "resumed"}`)
    );
  };
  const onMarkRead = (s) =>
    optimistic(s, { ...s, new_count: 0 }, () => markWatchSellerRead(s.seller_id), "Couldn't mark activity read").then(
      (ok) => ok && toast.success("Activity marked read")
    );
  const onRemove = async (s) => {
    const prevState = state;
    setState((st) => ({ ...st, sellers: st.sellers.filter((x) => x.seller_id !== s.seller_id), usage: Math.max(0, st.usage - 1) }));
    try {
      await removeWatchSeller(s.seller_id);
      toast.success("Seller removed");
    } catch {
      setState(prevState);
      toast.error("Couldn't remove seller");
    }
  };

  const onAdded = (res) => {
    const entry = res?.seller;
    setState((st) => {
      const rest = st.sellers.filter((x) => x.seller_id !== entry?.seller_id);
      return {
        ...st,
        sellers: entry ? [entry, ...rest] : rest,
        usage: Number(res?.usage) || st.usage,
        limit: Number(res?.limit) || st.limit,
      };
    });
    setAdding(false);
    toast.success("Seller added to Seller Watch");
  };

  const isSelected = (s) => selectedSellerIds.includes(s.seller_id);

  if (!open) return <SellerWatchLauncher count={unread} onClick={onOpen} />;

  const usagePct = Math.min(100, (state.usage / Math.max(1, state.limit)) * 100);

  return (
    <>
      <aside className="sw-dock" aria-label="Seller Watch" data-testid="sw-dock" ref={dockRef}>
        <div className="sw-head">
          <div className="sw-head-row">
            <div>
              <h2 className="sw-title">Seller Watch</h2>
              <div className="sw-sub">Monitor saved Walmart storefronts</div>
            </div>
            <button type="button" className="sw-icon-btn" onClick={onClose} aria-label="Close Seller Watch" data-testid="sw-close">
              ‹
            </button>
          </div>
          <div className="sw-toolbar">
            <button
              type="button"
              className="sw-icon-btn dark"
              aria-label="Add seller"
              title="Add seller"
              data-testid="sw-add"
              onClick={() => setAdding(true)}
            >
              +
            </button>
            <div className="sw-search">
              <span aria-hidden="true">⌕</span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search sellers"
                aria-label="Search sellers"
                data-testid="sw-search"
              />
            </div>
            <div className="sw-rel">
              <button
                type="button"
                className={`sw-icon-btn sw-menu-trigger${filter !== "all" ? " on" : ""}`}
                aria-label="Filter sellers"
                title="Filter"
                aria-haspopup="menu"
                aria-expanded={openMenu === "filter"}
                data-testid="sw-filter"
                onClick={() => setOpenMenu(openMenu === "filter" ? null : "filter")}
              >
                ≡
              </button>
              {openMenu === "filter" ? (
                <div className="sw-popover sw-list-menu" role="menu" aria-label="Filter sellers" data-testid="sw-filter-menu">
                  {FILTER_OPTIONS.map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={filter === id}
                      className={`sw-menu-item${filter === id ? " active" : ""}`}
                      onClick={() => {
                        setFilter(id);
                        setOpenMenu(null);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="sw-rel">
              <button
                type="button"
                className={`sw-icon-btn sw-menu-trigger${sort !== "recent" ? " on" : ""}`}
                aria-label="Sort sellers"
                title="Sort"
                aria-haspopup="menu"
                aria-expanded={openMenu === "sort"}
                data-testid="sw-sort"
                onClick={() => setOpenMenu(openMenu === "sort" ? null : "sort")}
              >
                ⇅
              </button>
              {openMenu === "sort" ? (
                <div className="sw-popover sw-list-menu sw-list-menu--right" role="menu" aria-label="Sort sellers" data-testid="sw-sort-menu">
                  {SORT_OPTIONS.map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={sort === id}
                      className={`sw-menu-item${sort === id ? " active" : ""}`}
                      onClick={() => {
                        setSort(id);
                        setOpenMenu(null);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <div className="sw-usage">
            <span>Seller Watch usage</span>
            <strong data-testid="sw-usage">
              {state.usage} / {state.limit} sellers
            </strong>
          </div>
          <div className="sw-usage-meter" aria-hidden="true">
            <i style={{ width: `${usagePct}%` }} />
          </div>
        </div>

        <div className="sw-tabs">
          {[
            ["all", "All Sellers", sellers.length],
            ["activity", "New Activity", activityCount],
          ].map(([id, label, n]) => (
            <button
              key={id}
              type="button"
              className={`sw-tab${tab === id ? " active" : ""}`}
              aria-pressed={tab === id}
              data-testid={`sw-tab-${id}`}
              onClick={() => setTab(id)}
            >
              <span>{label}</span>
              <span className="sw-count">{n}</span>
            </button>
          ))}
        </div>

        <div className="sw-list" data-testid="sw-list">
          {state.status === "loading" ? <div className="sw-empty">Loading sellers…</div> : null}
          {state.status === "error" ? (
            <div className="sw-empty">
              Couldn't load Seller Watch.
              <br />
              <button type="button" className="sw-btn" onClick={() => void load()}>
                Retry
              </button>
            </div>
          ) : null}
          {state.status === "ready" && !sellers.length ? (
            <div className="sw-empty" data-testid="sw-empty">
              No sellers yet. Analyze a seller or use + to add one.
            </div>
          ) : null}
          {state.status === "ready" && sellers.length && !visible.length ? (
            <div className="sw-empty" data-testid="sw-no-match">
              No sellers match these filters.
              <br />
              <button
                type="button"
                className="sw-btn"
                onClick={() => {
                  setFilter("all");
                  setTab("all");
                  setQuery("");
                }}
              >
                Clear filters
              </button>
            </div>
          ) : null}
          {visible.map((s) => (
            <SellerCard
              key={s.seller_id}
              seller={s}
              selected={isSelected(s)}
              days={daysBySeller[s.seller_id] || []}
              activeDayKey={hoverDay ?? pinnedDay}
              pinnedDayKey={pinnedDay}
              onHoverDay={setHoverDay}
              onPinDay={(key) => {
                setHoverDay(null);
                setPinnedDay((p) => (p === key ? null : key));
              }}
              openMenu={openMenu}
              setOpenMenu={setOpenMenu}
              knownTags={knownTags}
              onSelect={(seller) => onSelectSeller?.(seller)}
              onSave={onSave}
              onTags={onTags}
              onToggleStatus={onToggleStatus}
              onMarkRead={onMarkRead}
              onRemove={onRemove}
            />
          ))}
        </div>
      </aside>
      {adding ? (
        <AddSellerDialog
          usage={state.usage}
          limit={state.limit}
          onClose={() => setAdding(false)}
          onAdded={onAdded}
          onView={(id) => {
            setAdding(false);
            const s = sellers.find((x) => x.seller_id === id);
            onSelectSeller?.(s || { seller_id: id });
          }}
        />
      ) : null}
    </>
  );
}
